/**
 * The "buy a Publish Credit to publish this draft" intent — the only contract
 * between the wizard's Publish step and Checkout.
 *
 * StepPublish writes it just before leaving for Checkout (directly, or via
 * `/catalog` when no Collection matches the draft's template); CheckoutPage
 * reads it to (a) tag the Purchase with the draft's `invitationId` and
 * (b) offer "Continue to publish" once the payment is confirmed.
 *
 * sessionStorage, not URL params, because the catalog fallback path
 * (`/catalog` → `/catalog/:id` → `/checkout/:id`) and the login redirect
 * would otherwise have to thread query params through every intermediate
 * page — same precedent as `catalog/pendingSelection.ts`. Tab-scoped, so a
 * second tab never inherits another tab's intent.
 *
 * Everything read back is untrusted (same-origin script, devtools, stale
 * schema): `invitationId` must be a UUID (the backend DTO's own `@IsUUID`)
 * and `returnTo` must be a same-origin `/wizard` path — otherwise the
 * "Continue to publish" button would be an open redirect.
 */
import type { Collection } from '@/src/modules/catalog/types';

const STORAGE_KEY = 'invitecloud.buy_credit_intent.v1';

/**
 * Matches the backend Purchase expiry window (01_PURCHASE_DOMAIN.md §4,
 * 30 min) — an intent older than any Purchase it could have produced is an
 * abandoned visit and must not tag a later, unrelated checkout.
 */
export const BUY_CREDIT_INTENT_MAX_AGE_MS = 30 * 60 * 1000;

/** Query flag on the wizard return path: tells StepPublish to wait for the credit grant. */
export const CREDIT_PURCHASE_RETURN_PARAM = 'creditPurchase';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PARSE_BASE = 'https://invitecloud.invalid';

export interface BuyCreditIntent {
  invitationId: string;
  returnTo: string;
}

interface StoredIntent extends BuyCreditIntent {
  storedAt: number;
}

export function buildWizardReturnPath(draftId: string): string {
  return `/wizard?draftId=${encodeURIComponent(draftId)}&${CREDIT_PURCHASE_RETURN_PARAM}=1`;
}

export function isSafeWizardReturnPath(path: string): boolean {
  // Reject protocol-relative (`//host`) and backslash tricks browsers
  // normalise into `//host` before URL parsing ever sees them.
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return false;
  try {
    const url = new URL(path, PARSE_BASE);
    return url.origin === PARSE_BASE && url.pathname === '/wizard';
  } catch {
    return false;
  }
}

function isValidIntent(value: Partial<BuyCreditIntent>): value is BuyCreditIntent {
  return (
    typeof value.invitationId === 'string' &&
    UUID_RE.test(value.invitationId) &&
    typeof value.returnTo === 'string' &&
    isSafeWizardReturnPath(value.returnTo)
  );
}

export function setBuyCreditIntent(intent: BuyCreditIntent): void {
  if (!isValidIntent(intent)) return;
  try {
    const stored: StoredIntent = { ...intent, storedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable — checkout still works, just without the
    // draft tag or the "Continue to publish" shortcut.
  }
}

export function clearBuyCreditIntent(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}

/** Non-destructive read: a checkout-page refresh must keep the context. */
export function peekBuyCreditIntent(): BuyCreditIntent | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: Partial<StoredIntent>;
  try {
    parsed = JSON.parse(raw) as Partial<StoredIntent>;
  } catch {
    clearBuyCreditIntent();
    return null;
  }

  const age = typeof parsed.storedAt === 'number' ? Date.now() - parsed.storedAt : NaN;
  const fresh = age >= 0 && age <= BUY_CREDIT_INTENT_MAX_AGE_MS;
  if (!fresh || !isValidIntent(parsed)) {
    clearBuyCreditIntent();
    return null;
  }
  return { invitationId: parsed.invitationId, returnTo: parsed.returnTo };
}

/**
 * The Collection a "Buy a Publish Credit" click should check out: the active
 * Collection bound to the draft's own template, lowest `displayOrder` first
 * (the admin's merchandising order, same sort the catalog page uses).
 */
export function pickCollectionForTemplate(
  collections: readonly Collection[],
  templateId: string | undefined,
): Collection | null {
  if (!templateId) return null;
  const matches = collections.filter((c) => c.isActive && c.templateId === templateId);
  if (matches.length === 0) return null;
  return matches.reduce((best, c) => (c.displayOrder < best.displayOrder ? c : best));
}
